/**
 * Server previews. The server's renderer (the one real sends use) is the only
 * thing that resolves Liquid; the builder never renders a template itself.
 *
 * Requests are debounced while the author types. Two keys drive a read:
 *  - `identity` names WHAT is previewed (the template and the record, or the
 *    layout and the body placed in it). A different identity starts from a
 *    loading state: one template's or record's render is never shown for
 *    another.
 *  - `key` is the exact request (identity + the content being typed). While
 *    only the content changes, the last good render of the SAME identity stays
 *    on screen, marked stale, until the next one lands.
 * Every request carries a sequence number. Changing the request, disabling the
 * preview (null key) or unmounting invalidates the sequence, so a response that
 * arrives late is dropped instead of updating the screen.
 */
import { useEffect, useRef, useState } from "react";
import type {
  MessageLayoutPreviewResponse,
  MessageTemplatePreviewRecord,
  MessageTemplatePreviewResponse,
  MessageTemplateValidationResponse,
} from "@v0y4ge/api-contracts";
import { builderApi, classifyFailure, type Failure } from "./api";
import { toInput, type Work } from "./model";

export type PreviewState<T> =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "ready"; data: T; stale: boolean }
  | { phase: "error"; failure: Failure; data: T | null };

interface Held<T> { identity: string | null; state: PreviewState<T> }

export function useDebouncedRead<T>(identity: string | null, key: string | null, run: () => Promise<T>, delay: number): PreviewState<T> & { retry: () => void } {
  const [held, setHeld] = useState<Held<T>>({ identity: null, state: { phase: "idle" } });
  const seq = useRef(0);
  const runRef = useRef(run); runRef.current = run;
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const mine = ++seq.current;
    if (!key || !identity) {
      setHeld({ identity: null, state: { phase: "idle" } });
      return () => { seq.current++; };
    }
    setHeld((h) => {
      if (h.identity !== identity) return { identity, state: { phase: "loading" } };
      const s = h.state;
      if (s.phase === "ready") return { identity, state: { ...s, stale: true } };
      if (s.phase === "error" && s.data) return { identity, state: { phase: "ready", data: s.data, stale: true } };
      return { identity, state: { phase: "loading" } };
    });
    const timer = setTimeout(() => {
      runRef.current().then(
        (data) => { if (seq.current === mine) setHeld({ identity, state: { phase: "ready", data, stale: false } }); },
        (error: unknown) => {
          if (seq.current !== mine) return;
          setHeld((h) => ({ identity, state: { phase: "error", failure: classifyFailure(error), data: h.identity === identity && h.state.phase === "ready" ? h.state.data : null } }));
        },
      );
    }, delay);
    return () => { clearTimeout(timer); seq.current++; };
  }, [identity, key, delay, nonce]);

  // Render-time guard: whatever is held for another identity is never shown as this one's.
  const state: PreviewState<T> = !key || !identity ? { phase: "idle" } : held.identity === identity ? held.state : { phase: "loading" };
  return Object.assign({}, state, { retry: () => setNonce((n) => n + 1) });
}

export type TemplatePreview = MessageTemplatePreviewResponse["data"];
export type LayoutPreview = MessageLayoutPreviewResponse["data"];
export type TemplateValidation = MessageTemplateValidationResponse["data"];

/** Inline preview of the author's current content, against catalogue samples or one real record. */
export function useTemplatePreview(templateId: string | null, work: Work | null, record: MessageTemplatePreviewRecord | null, enabled: boolean) {
  const input = templateId && work && enabled ? { source: "inline" as const, content: toInput(work), ...(record ? { record } : {}) } : null;
  const identity = input ? JSON.stringify(["template", templateId, record ?? null]) : null;
  const key = input ? JSON.stringify([templateId, input]) : null;
  return useDebouncedRead<TemplatePreview>(identity, key, () => builderApi.getMessageTemplatePreview(templateId as string, input as NonNullable<typeof input>).then((r) => r.data), 450);
}

/** Server check of the author's current content: what would block publishing, with source positions. */
export function useTemplateValidation(templateId: string | null, work: Work | null, enabled: boolean) {
  const input = templateId && work && enabled ? { source: "inline" as const, content: toInput(work) } : null;
  const identity = input ? JSON.stringify(["validate", templateId]) : null;
  const key = input ? JSON.stringify(["validate", templateId, input]) : null;
  return useDebouncedRead<TemplateValidation>(identity, key, () => builderApi.getMessageTemplateValidation(templateId as string, input as NonNullable<typeof input>).then((r) => r.data), 700);
}

/** Server render of a layout (stored or unsaved) with a body placed literally into its slot. */
export function useLayoutPreview(layoutKey: string | null, html: string | null, bodyHtml: string | null, enabled: boolean) {
  const input = layoutKey && enabled ? { ...(html != null ? { html } : { version: "draft" as const }), ...(bodyHtml != null ? { bodyHtml } : {}) } : null;
  const identity = input ? JSON.stringify(["layout", layoutKey, bodyHtml ?? null]) : null;
  const key = input ? JSON.stringify([layoutKey, input]) : null;
  return useDebouncedRead<LayoutPreview>(identity, key, () => builderApi.getMessageLayoutPreview(layoutKey as string, input as NonNullable<typeof input>).then((r) => r.data), 450);
}
